import React, { forwardRef, type ReactNode, useId } from "react";
import { Button } from "@purpur/button";
import { FieldErrorText } from "@purpur/field-error-text";
import { FieldHelperText } from "@purpur/field-helper-text";
import { IconCheckCircleFilled } from "@purpur/icon/check-circle-filled";
import { IconClose } from "@purpur/icon/close";
import { Label } from "@purpur/label";
import { Spinner } from "@purpur/spinner";
import c from "classnames/bind";

import type { TextFieldBaseProps } from "./text-field";
import defaultStyles from "./text-field.module.scss";
import { useMutableRefObject } from "./utils";

const defaultRootClassName = "purpur-text-field-default";

// Internal overrides for variant composition (not part of the public API)
export type TextFieldBaseInternalOverrides = {
  _styles?: Record<string, string>;
  _rootClassName?: string;
  _prependEndAdornments?: ReactNode[];
  _inputModifiers?: Record<string, boolean>;
  _frameModifiers?: Record<string, boolean>;
  _inputContainerRef?: React.Ref<HTMLDivElement>;
};

export const TextFieldBase = forwardRef<
  HTMLInputElement,
  TextFieldBaseProps & TextFieldBaseInternalOverrides
>(
  (
    {
      ["data-testid"]: dataTestId,
      className,
      clearButtonAriaLabel,
      beforeField,
      afterField,
      endAdornment,
      errorText,
      helperText,
      hideRequiredAsterisk = false,
      label,
      loading = false,
      onClear,
      startAdornment,
      valid = false,
      negative = false,
      _styles,
      _rootClassName,
      _prependEndAdornments,
      _inputModifiers,
      _frameModifiers,
      _inputContainerRef,
      ...inputProps
    },
    ref
  ) => {
    const cx = c.bind(_styles ?? defaultStyles);
    const rootClassName = _rootClassName ?? defaultRootClassName;
    const randomId = useId();
    const inputId = inputProps.id ?? randomId;
    const getTestId = (name: string) => (dataTestId ? `${dataTestId}-${name}` : undefined);
    const isValid = valid && !errorText;
    const helperTextId = helperText ? `${inputId}-helper-text` : undefined;
    const startAdornments: ReactNode[] = [startAdornment].filter((adornment) => !!adornment);
    const hasValue =
      typeof inputProps.value === "number"
        ? inputProps.value !== undefined
        : inputProps.value?.length;
    const hasClearButton =
      hasValue &&
      !inputProps.disabled &&
      !inputProps.readOnly &&
      !loading &&
      onClear &&
      clearButtonAriaLabel;

    const internalRef = useMutableRefObject<HTMLInputElement | null>(null);
    const setRef = (node: HTMLInputElement | null) => {
      internalRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };
    const handleClear = () => {
      onClear?.();
      internalRef.current?.focus();
    };

    const localEndAdornments: ReactNode[] = [
      ...(_prependEndAdornments ?? []),
      loading && (
        <Spinner
          key="spinner"
          disabled={inputProps.disabled}
          size="xs"
          data-testid={getTestId("spinner")}
          negative={negative}
        />
      ),
      hasClearButton && (
        <Button
          negative={negative}
          key="clear-button"
          variant="tertiary-purple"
          onClick={handleClear}
          iconOnly
          aria-label={clearButtonAriaLabel ?? ""}
          data-testid={getTestId("clear-button")}
          tabIndex={-1}
        >
          <IconClose size="xs" />
        </Button>
      ),
      isValid && (
        <IconCheckCircleFilled
          key="valid-icon"
          data-testid={getTestId("valid-icon")}
          className={cx(`${rootClassName}__valid-icon`, {
            [`${rootClassName}__valid-icon--negative`]: negative,
          })}
        />
      ),
    ].filter((adornment) => !!adornment);

    const inputContainerClassnames = cx(`${rootClassName}__input-container`, {
      [`${rootClassName}__input-container--start-adornment`]: startAdornments.length,
      [`${rootClassName}__input-container--end-adornment`]:
        localEndAdornments.length || endAdornment,
      [`${rootClassName}__input-container--disabled`]: inputProps.disabled,
      [`${rootClassName}__input-container--has-clear-button`]: hasClearButton,
      [`${rootClassName}__input-container--readonly`]: inputProps.readOnly && !inputProps.disabled,
      [`${rootClassName}__input-container--negative`]: negative,
    });

    return (
      <div className={cx(className, rootClassName)}>
        {label && (
          <Label
            htmlFor={inputId}
            className={cx(`${rootClassName}__label`)}
            data-testid={getTestId("label")}
            disabled={inputProps.disabled}
            negative={negative}
          >
            {inputProps.required && !hideRequiredAsterisk && <span aria-hidden>*</span>}
            {label}
          </Label>
        )}
        <div className={cx(`${rootClassName}__field-row`)}>
          {!!beforeField && beforeField}
          <div ref={_inputContainerRef} className={inputContainerClassnames}>
            {!!startAdornments.length && (
              <div
                data-testid={getTestId("start-adornments")}
                className={cx(`${rootClassName}__adornment-container`)}
              >
                {startAdornments}
              </div>
            )}
            <input
              {...inputProps}
              id={inputId}
              ref={setRef}
              data-testid={getTestId("input")}
              aria-describedby={inputProps["aria-describedby"] || helperTextId}
              aria-invalid={inputProps["aria-invalid"] || !!errorText}
              className={cx([
                `${rootClassName}__input`,
                {
                  [`${rootClassName}__input--valid`]: isValid,
                  [`${rootClassName}__input--error`]: !!errorText,
                  [`${rootClassName}__input--negative`]: negative,
                  ...(_inputModifiers ?? {}),
                },
              ])}
            />
            <div
              className={cx(`${rootClassName}__frame`, {
                [`${rootClassName}__frame--negative`]: negative,
                ...(_frameModifiers ?? {}),
              })}
            />
            {(!!localEndAdornments.length || endAdornment) && (
              <div
                data-testid={getTestId("end-adornments")}
                className={cx(`${rootClassName}__adornment-container`)}
              >
                {localEndAdornments}
                {endAdornment}
              </div>
            )}
          </div>
          {!!afterField && afterField}
        </div>
        {helperTextId && (
          <FieldHelperText
            data-testid={getTestId("helper-text")}
            id={helperTextId}
            negative={negative}
            disabled={inputProps.disabled}
          >
            {helperText}
          </FieldHelperText>
        )}
        {errorText && (
          <FieldErrorText data-testid={getTestId("error-text")} negative={negative}>
            {errorText}
          </FieldErrorText>
        )}
      </div>
    );
  }
);

TextFieldBase.displayName = "TextField";
